import { useCallback, useEffect, useRef, useState } from 'react'
import { useDebouncedCallback } from 'use-debounce'
import { useFetchEntity } from '../../../../io/useEntities'
import getRegistryAsset from '../../../../io/api/registry/getRegistryAsset'
import { com } from '@eidu/web'
import { parseAssetReferences } from '@common/entity-content'
import { AssetLabel } from '@common/asset-references'
import entityIdFromString = com.eidu.entity.entityIdFromString

function extractUuids(text: string): Set<string> {
  return new Set(parseAssetReferences(text).map((reference) => reference.entityId))
}

const useAssetReferenceLabelResolver = (value: string) => {
  const [resolvedLabels, setResolvedLabels] = useState<Map<string, AssetLabel>>(new Map())
  const [unavailableUuids, setUnavailableUuids] = useState<ReadonlySet<string>>(new Set())
  const resolvedRef = useRef<Set<string>>(new Set())
  const fetchById = useFetchEntity()

  const resolve = useCallback(
    async (uuids: Set<string>) => {
      const newUuids = [...uuids].filter((id) => !resolvedRef.current.has(id))
      if (newUuids.length === 0) return

      const results = await Promise.allSettled(
        newUuids.map(async (uuid): Promise<{ uuid: string; label: AssetLabel | null }> => {
          const entity = await fetchById(entityIdFromString(uuid))
          if (entity) {
            return {
              uuid,
              label: {
                label: entity.primaryLabelText || uuid.slice(0, 8),
                typeId: entity.entity.typeId.toString(),
              },
            }
          }
          // Fall back to the cross-org registry — entity not visible in this org.
          try {
            const registryDetail = await getRegistryAsset(uuid)
            return {
              uuid,
              label: { label: registryDetail.name || uuid.slice(0, 8), typeId: undefined },
            }
          } catch {
            return { uuid, label: null }
          }
        })
      )

      setResolvedLabels((prev) => {
        const next = new Map(prev)
        for (const result of results) {
          if (result.status === 'fulfilled' && result.value.label) {
            const { uuid, label } = result.value
            next.set(uuid, label)
            resolvedRef.current.add(uuid)
          }
        }
        return next
      })

      setUnavailableUuids((prev) => {
        const next = new Set(prev)
        for (const result of results) {
          if (result.status !== 'fulfilled') continue
          if (result.value.label) next.delete(result.value.uuid)
          else next.add(result.value.uuid)
        }
        return next
      })
    },
    [fetchById]
  )

  const debouncedResolve = useDebouncedCallback((text: string) => {
    const uuids = extractUuids(text)
    resolve(uuids)
  }, 300)

  useEffect(() => {
    debouncedResolve(value)
  }, [value, debouncedResolve])

  return { resolvedLabels, unavailableUuids }
}

export default useAssetReferenceLabelResolver
